import classNames from "classnames";
import dayjs from "dayjs";
import { Form, Formik } from "formik";
import { FC, useEffect, useState } from "react";
import OtpInput from "react-otp-input";
import { object, string } from "zod";
import { toFormikValidationSchema } from "zod-formik-adapter";

import {
  useRequestEmailVerification,
  useVerifyEmailVerification,
} from "@/features/auth/components/onboarding-tabs/components/email-verification/hooks";
import { Button, Typography } from "@/shared/ui";
import { useOnboardingStore } from "@/stores";

import styles from "./email-verification.module.scss";
import { EmailVerificationFormScheme } from "./email-verification.types";

const validationScheme = object({
  code: string().min(4),
});

const EmailVerificationTab: FC = () => {
  const currentStep = useOnboardingStore((state) => state.currentStep);
  const { setCurrentStep, addPassedStep } = useOnboardingStore((state) => state.actions);

  const { mutate: verifyMutate, isPending } = useVerifyEmailVerification();
  const { mutate: requestCode } = useRequestEmailVerification();

  const [resendTimer, setResendTimer] = useState<number | null>(120000);
  const [isResendDisabled, setIsResendDisabled] = useState(true);
  const [showWarn, setShowWarn] = useState(false);

  useEffect(() => {
    let timer: NodeJS.Timeout;

    if (resendTimer) {
      setIsResendDisabled(true);
      timer = setInterval(() => {
        setResendTimer((prev) => {
          if (prev && prev > 1000) {
            const next = prev - 1000;

            if (next < 60000) {
              setShowWarn(true);
            }

            return next;
          } else {
            if (timer) clearInterval(timer);
            setIsResendDisabled(false);
            return null;
          }
        });
      }, 1000);
    }

    return () => {
      if (timer) clearInterval(timer);
    };
  }, [resendTimer]);

  const onSubmitHandler = (data: EmailVerificationFormScheme) => {
    verifyMutate(data, {
      onSuccess: () => {
        setCurrentStep(currentStep + 1);
        addPassedStep(currentStep);
      },
    });
  };

  const onResendHandler = async () => {
    requestCode(undefined, {
      onSuccess: ({ waitMs }) => {
        setResendTimer(waitMs);

        if (showWarn) {
          setShowWarn(false);
        }
      },
    });
  };

  const formatTime = (ms: number) => {
    const duration = dayjs.duration(ms);
    const minutes = duration.minutes();
    const seconds = duration.seconds();
    return `${minutes > 0 ? `${minutes}m ` : ""}${seconds}s`;
  };

  return (
    <main className={styles.emailVerificationTab}>
      <div className={styles.header}>
        <Typography semi variant="title-2">
          Verify your email
        </Typography>

        <Typography variant="caption-1">{`We already sent a code to your email`}</Typography>
      </div>

      <Formik<EmailVerificationFormScheme>
        initialValues={{ code: "" }}
        validationSchema={toFormikValidationSchema(validationScheme)}
        validateOnBlur={false}
        validateOnChange={false}
        onSubmit={onSubmitHandler}
      >
        {({ values, setFieldValue }) => (
          <Form className={styles.form}>
            <OtpInput
              shouldAutoFocus
              value={values.code}
              inputType="number"
              numInputs={4}
              containerStyle={styles.otp}
              renderInput={(props, index) => (
                <input
                  {...props}
                  className={classNames(styles.codeInput, props.value && styles.filled)}
                  onKeyDown={(e) => {
                    if (e.key === "Backspace" && e.currentTarget.value) {
                      e.preventDefault();

                      const replacedNumWithSpace = values.code.split("").toSpliced(index, 1, " ").join("");
                      setFieldValue("code", replacedNumWithSpace);
                    } else {
                      props.onKeyDown(e);
                    }
                  }}
                />
              )}
              onChange={(value) => setFieldValue("code", value)}
            />
            <Button
              fullWidth
              size="big"
              type="submit"
              className={styles.submitButton}
              disabled={isPending || values.code.length < 4}
            >
              {"Continue"}
            </Button>
            <div className={styles.footer}>
              <div className={styles.resend}>
                <Typography variant="caption-1" align="center">
                  Didn&apos;t receive the code?
                </Typography>
                <button type="button" disabled={isResendDisabled} onClick={onResendHandler}>
                  <Typography
                    variant="caption-1"
                    align="center"
                    className={classNames(styles.btn, isResendDisabled && styles.disabled)}
                  >
                    {resendTimer ? `Resend in ${formatTime(resendTimer)}` : "Resend"}
                  </Typography>
                </button>
              </div>

              {showWarn && (
                <Typography variant={"caption-1"} align="center" italic={true}>
                  If you haven’t received the email yet, please wait a few minutes or check spam.
                </Typography>
              )}
            </div>
          </Form>
        )}
      </Formik>
    </main>
  );
};

export { EmailVerificationTab };
